/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../utils/UNSAFE_classNames';
import { MessageSeverity, MessageVariant } from './Message.types';
import {
  MessageConfirmationIcon,
  MessageErrorIcon,
  MessageInfoIcon,
  MessageWarningIcon
} from '../UNSAFE_ThemedIcons';
import { Flex } from '../UNSAFE_Flex';

const messageStartIconStyles = cssProps`${{
  marginInlineEnd: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-2x)',
  display: 'flex',
  alignSelf: 'flex-start'
}}`;

const severityIconStyles = {
  banner: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-message-banner-start-icon-color)',
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-message-banner-start-icon-size)',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-message-banner-summary-line-height)'
  }}`,
  inline: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-message-inline-start-icon-color)',
    fontSize: '1rem'
  }}`,
  toast: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-message-toast-start-icon-color)',
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-message-toast-start-icon-size)',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-message-toast-start-icon-size)',
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-message-toast-start-icon-size)'
  }}`
};

const severityIcons = {
  confirmation: MessageConfirmationIcon,
  error: MessageErrorIcon,
  info: MessageInfoIcon,
  warning: MessageWarningIcon
};

const containerStyles = cssProps`${{
  height: '100%'
}}`;

/**
 * Props for the StartIcon component
 */
type Props = {
  /**
   * The icon severity
   */
  severity: Exclude<MessageSeverity, 'none'>;

  /**
   * Style variant
   */
  variant?: MessageVariant;

  /**
   * Translations resources
   * TODO: Replace with preact translations when it is available
   */
  translations?: {
    /**
     * Text for 'error' severity level
     */
    error?: string;

    /**
     * Text for 'warning' severity level
     */
    warning?: string;

    /**
     * Text for 'info' severity level
     */
    info?: string;

    /**
     * Text for 'confirmation' severity level
     */
    confirmation?: string;
  };
};

/**
 * StartIcon Component for rendering the severity based icon in Message
 */
function MessageStartIcon({ severity, variant = 'banner', translations }: Props) {
  const IconComponent = severityIcons[severity];
  // TODO: JET-50793
  const iconContent =
    variant !== 'inline' ? (
      <Flex align="center" height="100%">
        <span class={containerStyles} role="img" title={translations?.[severity]}>
          <IconComponent />
        </span>
      </Flex>
    ) : (
      <span class={containerStyles} role="img" title={translations?.[severity]}>
        <IconComponent />
      </span>
    );
  // If detail content is rendered, then wrap it in a div with specified style classes
  // TODO: JET-48715 - remove marker classes
  // Right now, oj-message-banner component uses this for unit tests and has no alternate way for
  // testing. This will need to be removed as we work on oj-c-message-banner.
  const markerClassForTests = 'oj-c-messagebanner-start-icon';
  return (
    <div
      class={classNames([
        variant === 'banner' && markerClassForTests,
        messageStartIconStyles,
        severityIconStyles[variant]
      ])}
      role="presentation">
      {iconContent}
    </div>
  );
}

export { MessageStartIcon };
